import { html } from '../../mixins/katapult-element';
import { ToTitleCase } from '../../modules/ToTitleCase';
import { KatapultDialog } from '../katapult-elements/katapult-dialog.js';
import { KatapultEditorTemplate } from './katapult-editor-template';

class KatapultLoadCaseEditor extends KatapultEditorTemplate {
  static get template() {
    return html`
      ${super.style}
      <style>
        :host {
          padding: 0;
          overflow: hidden;
        }
        #specSourceLabel {
          position: absolute;
          top: 0;
          right: 0;
          padding: 1.25em 1em;
          font-style: italic;
          color: var(--primary-text-color-faded);
          background: white;
        }
        #threePanelLayout {
          display: flex;
          flex-grow: 1;
          overflow: hidden;
          position: relative;
          background: white;
        }
        #threePanelLayout > div {
          display: flex;
          flex-direction: column;
          flex-basis: 0;
          flex-grow: 1;
          padding: 24px;
          overflow-y: auto;
          overflow-x: hidden;
        }
        #threePanelLayout > div:not(:last-child) {
          border-right: 2px solid rgba(0, 0, 0, 0.09);
        }
        paper-row {
          margin: 5px;
          padding: 5px;
        }
        #district {
          width: 33%;
          margin-left: 40px;
          margin-bottom: 25px;
        }
        #districtRow {
          margin: 0;
          padding: 1.25em 1em;
        }

        #title {
          text-transform: none;
          font-size: 14pt;
          color: var(--paper-grey-600);
          padding-left: 0;
          flex-grow: 0;
          flex-basis: auto;
        }
        #dot {
          display: flex;
          justify-content: center;
          align-items: center;
          height: 8px;
          width: 8px;
          font-size: 0;
          background: var(--paper-grey-400);
          color: white;
          border-radius: 100%;
          flex-shrink: 0;
          transition: all 0.3s;
        }
        paper-input {
          max-width: 30%;
        }
        input-element {
          width: 100%;
        }
      </style>

      <katapult-firebase-worker path="[[modelSpaceDetails.path]]/models/attributes/load_case" data="{{load_cases}}">
      </katapult-firebase-worker>
      <katapult-firebase-worker path="[[modelSpaceDetails.path]]/models/pole_loading/tension_districts" data="{{tensionDistricts}}">
      </katapult-firebase-worker>

      <template is="dom-if" if="[[itemData.source]]">
        <div id="specSourceLabel">Source: [[itemData.source]]</div>
      </template>
      <div style="overflow-y:auto; overflow-x:hidden; height:100%; padding:0 20px;">
        <paper-row id="districtRow">
          <paper-cell icon>
            <div id="dot"></div>
          </paper-cell>
          <div id="title">Load Case:</div>
        </paper-row>
        <paper-row light-grey style="border-radius:20px" outline id="district">
          <paper-cell>
            <input-element
              label="Select a load case"
              attribute_name="load_case"
              hide-attribute-name
              value="[[_itemData.load_case]]"
              data-path="_itemData.load_case"
              on-value-changed="updateProperty"
              model="[[load_cases]]"
              no-label-float
              disabled="[[!fullyEditable]]"
            ></input-element>
          </paper-cell>
        </paper-row>
        <paper-row id="districtRow">
          <paper-cell icon>
            <div id="dot"></div>
          </paper-cell>
          <div id="title">District:</div>
          <material-icon icon="help" style="margin-left:5px;">
            <katapult-tooltip>The district is used to select the wire spec tension tables to apply.</katapult-tooltip>
          </material-icon>
        </paper-row>
        <paper-row light-grey style="border-radius:20px" outline id="district">
          <paper-cell>
            <katapult-drop-down
              items="[[tensionDistricts]]"
              value-path="$key"
              label-path="$key"
              label-function="[[toTitleCase]]"
              label="Choose a district"
              value="{{_itemData.district}}"
              no-label-float
              on-value-changed="updateProperty"
            ></katapult-drop-down>
          </paper-cell>
        </paper-row>

        <paper-row style="border-radius: 16px 16px 0 0; margin 0;" grey divider>
          <paper-cell center>Environment Parameters</paper-cell>
          <paper-cell center>Load Factors</paper-cell>
          <paper-cell center>Strength Factors</paper-cell>
        </paper-row>

        <template is="dom-if" if="[[renderEditor]]" restamp>
          <div id="threePanelLayout">
            <div>
              <paper-row input light-grey rounded outline>
                <paper-cell>Analysis Temp</paper-cell>
                <paper-cell style="flex-direction: row-reverse">
                  <paper-input
                    type="text"
                    value="[[_itemData.environment_parameters.analysis_temp]]"
                    data-path="_itemData.environment_parameters.analysis_temp"
                    on-value-changed="updateProperty"
                    no-label-float
                    disabled="[[!fullyEditable]]"
                    style="margin-right: 2em; max-width:70px;"
                    auto-validate
                    pattern="^-?([0-9]+([.][0-9]*)?|[.][0-9]+)$"
                  >
                    <span style="pointer-events:none;" slot="suffix">°F</span>
                  </paper-input>
                </paper-cell>
              </paper-row>
              <paper-row input light-grey rounded outline>
                <paper-cell>Horizontal Wind Pressure</paper-cell>
                <paper-cell style="flex-direction: row-reverse">
                  <paper-input
                    value="[[_itemData.environment_parameters.horizontal_wind_pressure]]"
                    data-path="_itemData.environment_parameters.horizontal_wind_pressure"
                    on-value-changed="updateProperty"
                    no-label-float
                    disabled="[[!fullyEditable]]"
                    style="margin-right: 2em; max-width:70px;"
                    auto-validate
                    pattern="^-?([0-9]+([.][0-9]*)?|[.][0-9]+)$"
                  >
                    <span style="pointer-events:none;" slot="suffix">lbf/ft²</span>
                  </paper-input>
                </paper-cell>
              </paper-row>
              <paper-row input light-grey rounded outline>
                <paper-cell>Radial Ice Thickness</paper-cell>
                <paper-cell style="flex-direction: row-reverse">
                  <paper-input
                    value="[[_itemData.environment_parameters.radial_ice_thickness]]"
                    data-path="_itemData.environment_parameters.radial_ice_thickness"
                    on-value-changed="updateProperty"
                    no-label-float
                    disabled="[[!fullyEditable]]"
                    style="margin-right: 2em; max-width:70px;"
                    auto-validate
                    pattern="^-?([0-9]+([.][0-9]*)?|[.][0-9]+)$"
                  >
                    <span style="pointer-events:none;" slot="suffix">in</span>
                  </paper-input>
                </paper-cell>
              </paper-row>
              <paper-row input light-grey rounded outline>
                <paper-cell>Ice Density</paper-cell>
                <paper-cell style="flex-direction: row-reverse">
                  <paper-input
                    value="[[_itemData.environment_parameters.ice_density]]"
                    data-path="_itemData.environment_parameters.ice_density"
                    on-value-changed="updateProperty"
                    no-label-float
                    disabled="[[!fullyEditable]]"
                    style="margin-right: 2em; max-width:70px;"
                    auto-validate
                    pattern="^-?([0-9]+([.][0-9]*)?|[.][0-9]+)$"
                  >
                    <span style="pointer-events:none;" slot="suffix">lbf/ft³</span>
                  </paper-input>
                </paper-cell>
              </paper-row>
            </div>

            <div>
              <paper-row input light-grey rounded outline>
                <paper-cell>Conductor Constant</paper-cell>
                <paper-cell style="flex-direction: row-reverse">
                  <paper-input
                    value="[[_itemData.load_factors.conductor_constant]]"
                    data-path="_itemData.load_factors.conductor_constant"
                    on-value-changed="updateProperty"
                    no-label-float
                    disabled="[[!fullyEditable]]"
                    style="margin-right: 2em"
                    auto-validate
                    pattern="^-?([0-9]+([.][0-9]*)?|[.][0-9]+)$"
                  >
                  </paper-input>
                </paper-cell>
              </paper-row>
              <paper-row input light-grey rounded outline>
                <paper-cell>Guying</paper-cell>
                <paper-cell style="flex-direction: row-reverse">
                  <paper-input
                    value="[[_itemData.load_factors.guying]]"
                    data-path="_itemData.load_factors.guying"
                    on-value-changed="updateProperty"
                    no-label-float
                    disabled="[[!fullyEditable]]"
                    style="margin-right: 2em"
                    auto-validate
                    pattern="^-?([0-9]+([.][0-9]*)?|[.][0-9]+)$"
                  >
                  </paper-input>
                </paper-cell>
              </paper-row>
              <paper-row input light-grey rounded outline>
                <paper-cell>General Longitudinal Loads</paper-cell>
                <paper-cell style="flex-direction: row-reverse">
                  <paper-input
                    value="[[_itemData.load_factors.general_longitudinal_loads]]"
                    data-path="_itemData.load_factors.general_longitudinal_loads"
                    on-value-changed="updateProperty"
                    no-label-float
                    disabled="[[!fullyEditable]]"
                    style="margin-right: 2em"
                    auto-validate
                    pattern="^-?([0-9]+([.][0-9]*)?|[.][0-9]+)$"
                  >
                  </paper-input>
                </paper-cell>
              </paper-row>
              <paper-row input light-grey rounded outline>
                <paper-cell>Longitudinal Deadend Loads</paper-cell>
                <paper-cell style="flex-direction: row-reverse">
                  <paper-input
                    value="[[_itemData.load_factors.longitudinal_deadend_loads]]"
                    data-path="_itemData.load_factors.longitudinal_deadend_loads"
                    on-value-changed="updateProperty"
                    no-label-float
                    disabled="[[!fullyEditable]]"
                    style="margin-right: 2em"
                    auto-validate
                    pattern="^-?([0-9]+([.][0-9]*)?|[.][0-9]+)$"
                  >
                  </paper-input>
                </paper-cell>
              </paper-row>
              <paper-row input light-grey rounded outline>
                <paper-cell>Transverse Wind Loads</paper-cell>
                <paper-cell style="flex-direction: row-reverse">
                  <paper-input
                    value="[[_itemData.load_factors.transverse_wind_loads]]"
                    data-path="_itemData.load_factors.transverse_wind_loads"
                    on-value-changed="updateProperty"
                    no-label-float
                    disabled="[[!fullyEditable]]"
                    style="margin-right: 2em"
                    auto-validate
                    pattern="^-?([0-9]+([.][0-9]*)?|[.][0-9]+)$"
                  >
                  </paper-input>
                </paper-cell>
              </paper-row>
              <paper-row input light-grey rounded outline>
                <paper-cell>Transverse Wire Loads</paper-cell>
                <paper-cell style="flex-direction: row-reverse">
                  <paper-input
                    value="[[_itemData.load_factors.transverse_wire_loads]]"
                    data-path="_itemData.load_factors.transverse_wire_loads"
                    on-value-changed="updateProperty"
                    no-label-float
                    disabled="[[!fullyEditable]]"
                    style="margin-right: 2em"
                    auto-validate
                    pattern="^-?([0-9]+([.][0-9]*)?|[.][0-9]+)$"
                  >
                  </paper-input>
                </paper-cell>
              </paper-row>
              <paper-row input light-grey rounded outline>
                <paper-cell>Vertical Loads</paper-cell>
                <paper-cell style="flex-direction: row-reverse">
                  <paper-input
                    value="[[_itemData.load_factors.vertical_loads]]"
                    data-path="_itemData.load_factors.vertical_loads"
                    on-value-changed="updateProperty"
                    no-label-float
                    disabled="[[!fullyEditable]]"
                    style="margin-right: 2em"
                  >
                  </paper-input>
                </paper-cell>
              </paper-row>
            </div>

            <div>
              <paper-row input light-grey rounded outline>
                <paper-cell>Fiber Reinforced Polymer</paper-cell>
                <paper-cell style="flex-direction: row-reverse">
                  <paper-input
                    value="[[_itemData.strength_factors.fiber_reinforced_polymer]]"
                    data-path="_itemData.strength_factors.fiber_reinforced_polymer"
                    on-value-changed="updateProperty"
                    no-label-float
                    disabled="[[!fullyEditable]]"
                    style="margin-right: 2em"
                    auto-validate
                    pattern="^-?([0-9]+([.][0-9]*)?|[.][0-9]+)$"
                  >
                  </paper-input>
                </paper-cell>
              </paper-row>
              <paper-row input light-grey rounded outline>
                <paper-cell>Foundation</paper-cell>
                <paper-cell style="flex-direction: row-reverse">
                  <paper-input
                    value="[[_itemData.strength_factors.foundation]]"
                    data-path="_itemData.strength_factors.foundation"
                    on-value-changed="updateProperty"
                    no-label-float
                    disabled="[[!fullyEditable]]"
                    style="margin-right: 2em"
                    auto-validate
                    pattern="^-?([0-9]+([.][0-9]*)?|[.][0-9]+)$"
                  >
                  </paper-input>
                </paper-cell>
              </paper-row>
              <paper-row input light-grey rounded outline>
                <paper-cell>Guy Anchors</paper-cell>
                <paper-cell style="flex-direction: row-reverse">
                  <paper-input
                    value="[[_itemData.strength_factors.guy_anchors]]"
                    data-path="_itemData.strength_factors.guy_anchors"
                    on-value-changed="updateProperty"
                    no-label-float
                    disabled="[[!fullyEditable]]"
                    style="margin-right: 2em"
                    auto-validate
                    pattern="^-?([0-9]+([.][0-9]*)?|[.][0-9]+)$"
                  >
                  </paper-input>
                </paper-cell>
              </paper-row>
              <paper-row input light-grey rounded outline>
                <paper-cell>Guy Wire</paper-cell>
                <paper-cell style="flex-direction: row-reverse">
                  <paper-input
                    value="[[_itemData.strength_factors.guy_wire]]"
                    data-path="_itemData.strength_factors.guy_wire"
                    on-value-changed="updateProperty"
                    no-label-float
                    disabled="[[!fullyEditable]]"
                    style="margin-right: 2em"
                    auto-validate
                    pattern="^-?([0-9]+([.][0-9]*)?|[.][0-9]+)$"
                  >
                  </paper-input>
                </paper-cell>
              </paper-row>
              <paper-row input light-grey rounded outline>
                <paper-cell>Metal</paper-cell>
                <paper-cell style="flex-direction: row-reverse">
                  <paper-input
                    value="[[_itemData.strength_factors.metal]]"
                    data-path="_itemData.strength_factors.metal"
                    on-value-changed="updateProperty"
                    no-label-float
                    disabled="[[!fullyEditable]]"
                    style="margin-right: 2em"
                    auto-validate
                    pattern="^-?([0-9]+([.][0-9]*)?|[.][0-9]+)$"
                  >
                  </paper-input>
                </paper-cell>
              </paper-row>
              <paper-row input light-grey rounded outline>
                <paper-cell>Prestressed Concrete</paper-cell>
                <paper-cell style="flex-direction: row-reverse">
                  <paper-input
                    value="[[_itemData.strength_factors.prestressed_concrete]]"
                    data-path="_itemData.strength_factors.prestressed_concrete"
                    on-value-changed="updateProperty"
                    no-label-float
                    disabled="[[!fullyEditable]]"
                    style="margin-right: 2em"
                    auto-validate
                    pattern="^-?([0-9]+([.][0-9]*)?|[.][0-9]+)$"
                  >
                  </paper-input>
                </paper-cell>
              </paper-row>
              <paper-row input light-grey rounded outline>
                <paper-cell>Reinforced Concrete</paper-cell>
                <paper-cell style="flex-direction: row-reverse">
                  <paper-input
                    value="[[_itemData.strength_factors.reinforced_concrete]]"
                    data-path="_itemData.strength_factors.reinforced_concrete"
                    on-value-changed="updateProperty"
                    no-label-float
                    disabled="[[!fullyEditable]]"
                    style="margin-right: 2em"
                    auto-validate
                    pattern="^-?([0-9]+([.][0-9]*)?|[.][0-9]+)$"
                  >
                  </paper-input>
                </paper-cell>
              </paper-row>
              <paper-row input light-grey rounded outline>
                <paper-cell>Support Hardware</paper-cell>
                <paper-cell style="flex-direction: row-reverse">
                  <paper-input
                    value="[[_itemData.strength_factors.support_hardware]]"
                    data-path="_itemData.strength_factors.support_hardware"
                    on-value-changed="updateProperty"
                    no-label-float
                    disabled="[[!fullyEditable]]"
                    style="margin-right: 2em"
                    auto-validate
                    pattern="^-?([0-9]+([.][0-9]*)?|[.][0-9]+)$"
                  >
                  </paper-input>
                </paper-cell>
              </paper-row>
              <paper-row input light-grey rounded outline>
                <paper-cell>Wood</paper-cell>
                <paper-cell style="flex-direction: row-reverse">
                  <paper-input
                    value="[[_itemData.strength_factors.wood]]"
                    data-path="_itemData.strength_factors.wood"
                    on-value-changed="updateProperty"
                    no-label-float
                    disabled="[[!fullyEditable]]"
                    style="margin-right: 2em"
                    auto-validate
                    pattern="^-?([0-9]+([.][0-9]*)?|[.][0-9]+)$"
                  >
                  </paper-input>
                </paper-cell>
              </paper-row>
            </div>
          </div>
        </template>
      </div>
      ${super.createItemContainer}
    `;
  }
  static get properties() {
    return {
      cases: {
        type: Object,
        value: {
          environment_parameters: {
            analysis_temp: 0,
            horizontal_wind_pressure: 0,
            radial_ice_thickness: 0,
            ice_density: 57
          },
          load_factors: {
            conductor_constant: 0,
            guying: 1,
            general_longitudinal_loads: 1,
            longitudinal_deadend_loads: 1,
            transverse_wind_loads: 1,
            transverse_wire_loads: 1,
            vertical_loads: 1
          },
          strength_factors: {
            fiber_reinforced_polymer: 1,
            foundation: 1,
            guy_anchors: 1,
            guy_wire: 1,
            metal: 1,
            prestressed_concrete: 1,
            reinforced_concrete: 1,
            support_hardware: 1,
            wood: 1
          }
        }
      }
    };
  }
  static get observers() {}
  constructor() {
    super();
    this.computedLabel = {
      dependencies: [{ path: 'load_case' }],
      compute: (spec) => spec ?? 'Untitled Load Case'
    };
  }

  async beforeSave() {
    const districtEnvironment = this.tensionDistricts?.[this._itemData.district];
    if (districtEnvironment) {
      const mismatchedParameters = Object.keys(districtEnvironment).filter(
        (key) =>
          !['$key', 'conductor_constant'].includes(key) &&
          parseFloat(districtEnvironment[key]) !== parseFloat(this._itemData.environment_parameters[key])
      );
      if (mismatchedParameters.length > 0) {
        const dialogConfig = KatapultDialog.confirm({
          dialog: { title: 'District Environment Parameters Mismatch', draggable: true },
          body: `The environment parameters for this load case do not match the environment parameters for the selected district (${mismatchedParameters
            .map((x) => `${ToTitleCase(x)}: ${districtEnvironment[x]}`)
            .join(', ')}). Are you sure you want to continue?`,
          confirmButton: { label: 'Save' }
        });
        const didConfirm = await dialogConfig.confirmed;
        if (!didConfirm) throw new Error('Save Canceled by User');
      }
    }

    let update = {};
    this._itemData.source = `Manual Entry - ${katapultAuth.user.email} (${new Date().toLocaleDateString()})`;
    this._itemData.$key = this._itemData.load_case;
    for (let key in this.cases.environment_parameters) {
      this.convertProperty({
        detail: { value: this._itemData.environment_parameters[key], path: `_itemData.environment_parameters.${key}` }
      });
      if (!this._itemData.environment_parameters[key]) this._itemData.environment_parameters[key] = this.cases.environment_parameters[key];
      update[`environment_parameters/${key}`] = this._itemData.environment_parameters[key];
    }
    for (let key in this.cases.load_factors) {
      this.convertProperty({ detail: { value: this._itemData.load_factors[key], path: `_itemData.load_factors.${key}` } });
      if (!this._itemData.load_factors[key]) this._itemData.load_factors[key] = this.cases.load_factors[key];
      update[`load_factors/${key}`] = this._itemData.load_factors[key];
    }
    for (let key in this.cases.strength_factors) {
      this.convertProperty({ detail: { value: this._itemData.strength_factors[key], path: `_itemData.strength_factors.${key}` } });
      if (!this._itemData.strength_factors[key]) this._itemData.strength_factors[key] = this.cases.strength_factors[key];
      update[`strength_factors/${key}`] = this._itemData.strength_factors[key];
    }
    FirebaseWorker.ref(`${this.modelSpaceDetails.path}/models/pole_loading/load_cases/${this._itemData.$key}`).update(update);
  }
  convertProperty(e) {
    let val = e.detail.value;
    let parsedVal = parseFloat(val);
    if (isNaN(parsedVal)) {
      parsedVal = 0;
    }
    e.detail.value = parsedVal;
    this.updateProperty(e);
  }
  toTitleCase(str) {
    return ToTitleCase(str);
  }
}

customElements.define('katapult-load-case-editor', KatapultLoadCaseEditor);
